import React, { useState } from 'react';
import {
  Box, Typography, Accordion, AccordionSummary, AccordionDetails,
  TextField, Button, Divider, Chip, Tooltip, Select, MenuItem,
  Dialog, DialogTitle, DialogContent,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GridOnIcon from '@mui/icons-material/GridOn';
import TuneIcon from '@mui/icons-material/Tune';
import SaveIcon from '@mui/icons-material/Save';
import PolylineIcon from '@mui/icons-material/Polyline';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import IconButton from '@mui/material/IconButton';
import { METERS_TO_PX, GRID_SIZE } from './constants';
import ModulePalette from './ModulePalette';
import ColorFilterPanel from './ColorFilterPanel';

// ─── Polygon preset generators ─────────────────────────────────────────────────

function snap(v) {
  return Math.round(v / GRID_SIZE) * GRID_SIZE;
}

const PRESETS = [
  {
    id: 'rect',
    label: 'Téglalap',
    title: 'Egyszerű téglalap',
    preview: '▬',
    make: (w, h) => null, // null = no polygon (plain rectangle)
  },
  {
    id: 'L_BR',
    label: 'L (jobb-le)',
    title: 'L-alak: jobb alsó sarok levágva',
    preview: '⌐',
    make: (w, h) => [
      { x: 0, y: 0 },
      { x: w, y: 0 },
      { x: w, y: snap(h * 0.55) },
      { x: snap(w * 0.55), y: snap(h * 0.55) },
      { x: snap(w * 0.55), y: h },
      { x: 0, y: h },
    ],
  },
  {
    id: 'L_BL',
    label: 'L (bal-le)',
    title: 'L-alak: bal alsó sarok levágva',
    preview: '¬',
    make: (w, h) => [
      { x: 0, y: 0 },
      { x: w, y: 0 },
      { x: w, y: h },
      { x: snap(w * 0.45), y: h },
      { x: snap(w * 0.45), y: snap(h * 0.55) },
      { x: 0, y: snap(h * 0.55) },
    ],
  },
  {
    id: 'L_TR',
    label: 'L (jobb-fel)',
    title: 'L-alak: jobb felső sarok levágva',
    preview: '⌐',
    make: (w, h) => [
      { x: 0, y: 0 },
      { x: snap(w * 0.55), y: 0 },
      { x: snap(w * 0.55), y: snap(h * 0.45) },
      { x: w, y: snap(h * 0.45) },
      { x: w, y: h },
      { x: 0, y: h },
    ],
  },
  {
    id: 'L_TL',
    label: 'L (bal-fel)',
    title: 'L-alak: bal felső sarok levágva',
    preview: '¬',
    make: (w, h) => [
      { x: snap(w * 0.45), y: 0 },
      { x: w, y: 0 },
      { x: w, y: h },
      { x: 0, y: h },
      { x: 0, y: snap(h * 0.45) },
      { x: snap(w * 0.45), y: snap(h * 0.45) },
    ],
  },
  {
    id: 'U',
    label: 'U-alak',
    title: 'U-alak: középső rész kivágva alulról',
    preview: 'U',
    make: (w, h) => [
      { x: 0, y: 0 },
      { x: w, y: 0 },
      { x: w, y: h },
      { x: snap(w * 0.65), y: h },
      { x: snap(w * 0.65), y: snap(h * 0.45) },
      { x: snap(w * 0.35), y: snap(h * 0.45) },
      { x: snap(w * 0.35), y: h },
      { x: 0, y: h },
    ],
  },
  {
    id: 'T',
    label: 'T-alak',
    title: 'T-alak: középső oszlop',
    preview: 'T',
    make: (w, h) => [
      { x: 0, y: 0 },
      { x: w, y: 0 },
      { x: w, y: snap(h * 0.4) },
      { x: snap(w * 0.65), y: snap(h * 0.4) },
      { x: snap(w * 0.65), y: h },
      { x: snap(w * 0.35), y: h },
      { x: snap(w * 0.35), y: snap(h * 0.4) },
      { x: 0, y: snap(h * 0.4) },
    ],
  },
];

/**
 * Left toolbar: venue setup, module palette, color filter.
 *
 * Props:
 *   venue              – { width, height, polygon? }
 *   onVenueSizeChange  – (widthPx, heightPx) => void
 *   onSetPolygon       – (polygon | null) => void
 *   activeFilter
 *   onFilterChange
 *   isDirty
 *   onZoomReset
 *   polygonEditMode
 *   onTogglePolygonEdit
 *   onClearPolygon
 *   onClose            – ha meg van adva, bezárás (X) gomb jelenik meg a fejlécben (mobil drawer)
 *   stacked            – álló módban (telefon/tablet): teljes szélesség, alsó szegély oldalsó helyett
 *   toolboxIconSize    – stacked toolbox ikonméret px-ben (telefonon kisebbre állítva, hogy a
 *                        teljes sor elférjen görgetés nélkül a keskeny képernyőn)
 */
export default function SeatingToolbar({
  venue,
  onVenueSizeChange,
  onSetPolygon,
  activeFilter,
  onFilterChange,
  isDirty,
  onZoomReset,
  polygonEditMode,
  onTogglePolygonEdit,
  onClearPolygon,
  onPaletteItemClick,
  onClose,
  stacked = false,
  toolboxIconSize = 44,
}) {
  const [venueW, setVenueW] = useState(Math.round(venue.width / METERS_TO_PX));
  const [venueH, setVenueH] = useState(Math.round(venue.height / METERS_TO_PX));
  // Stacked (tablet álló) toolbox nézetben a helyszín/szín beállítások egy dialógusba kerülnek,
  // hogy a felső sáv csak az asztal-ikonokat mutassa, görgetés nélkül.
  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleVenueSave = () => {
    const w = Math.max(5, parseInt(venueW, 10) || 20);
    const h = Math.max(5, parseInt(venueH, 10) || 15);
    onVenueSizeChange(w * METERS_TO_PX, h * METERS_TO_PX);
    setVenueW(w);
    setVenueH(h);
  };

  const handlePreset = (preset) => {
    const w = venue.width;
    const h = venue.height;
    onSetPolygon(preset.make(w, h));
  };

  // A helyszín alak (előre gyártott + kézi pont szerkesztés) és a szín szűrő mindig a
  // fogaskerék-gombos dialógusban él (ennyi beállításnak nincs helye a felső sávban).
  // A helyszín MÉRETE viszont csak álló mobil/tablet (stacked) nézetben van itt — fekvő/
  // desktop nézetben ugyanez a mező közvetlenül a felső sávban látszik (lásd lejjebb).
  const settingsBody = (
    <>
      {stacked && (
        <>
          <Accordion defaultExpanded disableGutters sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, px: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <GridOnIcon sx={{ fontSize: 16, color: '#2a5d32' }} />
                <Typography variant="caption" fontWeight={700}>Helyszín mérete</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1.5, pb: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  label="A oldal (m)"
                  type="number"
                  value={venueW}
                  onChange={e => setVenueW(e.target.value)}
                  size="small"
                  inputProps={{ min: 5, max: 200 }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="B oldal (m)"
                  type="number"
                  value={venueH}
                  onChange={e => setVenueH(e.target.value)}
                  size="small"
                  inputProps={{ min: 5, max: 200 }}
                  sx={{ flex: 1 }}
                />
              </Box>
              <Button size="small" variant="outlined" fullWidth onClick={handleVenueSave} sx={{ fontSize: 11 }}>
                Alkalmaz
              </Button>
            </AccordionDetails>
          </Accordion>

          <Divider />
        </>
      )}

      {/* Venue Shape */}
      <Accordion disableGutters sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, px: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <PolylineIcon sx={{ fontSize: 16, color: '#2a5d32' }} />
            <Typography variant="caption" fontWeight={700}>Helyszín alakja</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 1, pb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, display: 'block', mb: 1 }}>
            Válassz előre beállított formát, vagy szerkeszd kézzel.
          </Typography>

          {/* Preset shape grid */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {PRESETS.map(preset => (
              <Tooltip key={preset.id} title={preset.title} placement="top">
                <Button
                  size="small"
                  variant={
                    (preset.id === 'rect' && !venue.polygon) ||
                    (preset.id !== 'rect' && venue.polygon)
                      ? 'contained'
                      : 'outlined'
                  }
                  color="inherit"
                  onClick={() => handlePreset(preset)}
                  sx={{
                    minWidth: 0,
                    width: 56,
                    height: 40,
                    flexDirection: 'column',
                    fontSize: 16,
                    lineHeight: 1,
                    gap: 0.25,
                    px: 0.5,
                    py: 0.5,
                    '&.MuiButton-contained': { bgcolor: '#e8f5e9', color: '#2a5d32', border: '1.5px solid #2a5d32' },
                  }}
                >
                  <span style={{ fontSize: 18, lineHeight: 1 }}>{preset.preview}</span>
                  <span style={{ fontSize: 8, lineHeight: 1 }}>{preset.label}</span>
                </Button>
              </Tooltip>
            ))}
          </Box>

          {/* Manual polygon edit toggle */}
          <Button
            size="small"
            variant={polygonEditMode ? 'contained' : 'outlined'}
            fullWidth
            color={polygonEditMode ? 'success' : 'inherit'}
            onClick={onTogglePolygonEdit}
            sx={{ fontSize: 11, mb: venue?.polygon ? 0.5 : 0 }}
          >
            {polygonEditMode ? '✓ Szerkesztés befejezve' : 'Kézi pont szerkesztés'}
          </Button>
          {venue?.polygon && (
            <Button
              size="small"
              variant="text"
              color="error"
              fullWidth
              onClick={onClearPolygon}
              sx={{ fontSize: 11 }}
            >
              Visszaállítás téglalap alakra
            </Button>
          )}
        </AccordionDetails>
      </Accordion>

      <Divider />

      {/* Color Filter */}
      <Accordion defaultExpanded disableGutters sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, px: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <TuneIcon sx={{ fontSize: 16, color: '#2a5d32' }} />
            <Typography variant="caption" fontWeight={700}>Szín szűrő</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 1.5, pb: 1.5 }}>
          <ColorFilterPanel activeFilter={activeFilter} onFilterChange={onFilterChange} />
        </AccordionDetails>
      </Accordion>
    </>
  );

  // Fekvő/desktop (!stacked) nézetben a helyszín szélessége/magassága + egy gyors
  // alak-választó közvetlenül a sávban látszik (elfér a hely), nem a dialógusban.
  const currentPresetValue = venue?.polygon ? '__custom__' : 'rect';
  const inlineVenueControls = !stacked && (
    <>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
      <TextField
        label="A (m)"
        type="number"
        value={venueW}
        onChange={e => setVenueW(e.target.value)}
        size="small"
        inputProps={{ min: 5, max: 200 }}
        sx={{ width: 72 }}
      />
      <Typography variant="caption" color="text.secondary">×</Typography>
      <TextField
        label="B (m)"
        type="number"
        value={venueH}
        onChange={e => setVenueH(e.target.value)}
        size="small"
        inputProps={{ min: 5, max: 200 }}
        sx={{ width: 72 }}
      />
      <Tooltip title="Helyszín méretének alkalmazása">
        <IconButton size="small" onClick={handleVenueSave} color="primary">
          <CheckIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
      <Select
        value={currentPresetValue}
        onChange={e => {
          const preset = PRESETS.find(p => p.id === e.target.value);
          if (preset) handlePreset(preset);
        }}
        size="small"
        displayEmpty
        sx={{ minWidth: 120, fontSize: 13 }}
      >
        {venue?.polygon && (
          <MenuItem value="__custom__" disabled sx={{ fontStyle: 'italic' }}>
            Egyéni alak
          </MenuItem>
        )}
        {PRESETS.map(preset => (
          <MenuItem key={preset.id} value={preset.id}>
            {preset.preview}&nbsp;&nbsp;{preset.label}
          </MenuItem>
        ))}
      </Select>
    </>
  );

  // ─── Vízszintes eszköztár-sáv a vászon fölött, minden nézetben. Álló mobil/tablet
  // (stacked) nézetben csak a paletta-ikonok látszanak, a többi a fogaskerék-gombos
  // dialógusban; fekvő/desktop nézetben a helyszín méret/alak mezők is a sávban vannak. ──
  return (
    <>
      <Box
        sx={{
          width: '100%',
          flexShrink: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: '#fafaf7',
          display: 'flex',
          alignItems: 'center',
          gap: toolboxIconSize < 44 ? 0.5 : 1,
          px: toolboxIconSize < 44 ? 0.5 : 1,
          py: toolboxIconSize < 44 ? 0.5 : 1,
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
      >
        <ModulePalette onItemClick={onPaletteItemClick} orientation="row" size={toolboxIconSize} />
        {inlineVenueControls}
        <Box sx={{ flex: 1, minWidth: 4 }} />
        {isDirty && (
          <Chip
            icon={<SaveIcon sx={{ fontSize: 12 }} />}
            label="Mentés..."
            size="small"
            color="warning"
            variant="outlined"
            sx={{ fontSize: 10, height: 20, flexShrink: 0 }}
          />
        )}
        <Tooltip title={stacked ? 'Helyszín és szín beállítások' : 'Helyszín alakja és szín szűrő'}>
          <IconButton size="small" onClick={() => setSettingsOpen(true)} sx={{ flexShrink: 0 }}>
            <TuneIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {onClose && (
          <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          {stacked ? 'Helyszín és szín beállítások' : 'Helyszín alakja és szín szűrő'}
          <IconButton size="small" onClick={() => setSettingsOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {settingsBody}
        </DialogContent>
      </Dialog>
    </>
  );
}
